import { Route } from 'react-router-dom';
import { createApp } from '@backstage/app-defaults';
import { AppRouter } from '@backstage/core-app-api';
import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { DEFAULT_NAMESPACE } from '@appfolio/backstage-plugin-catalog-common';
import { QueryClientProvider } from '@tanstack/react-query';

// Layout components
import { NotFoundErrorPage, Root, SignInPage } from './core/layout';

// Core
import queryClient from './core/queryClient';
import apis from './core/apis';
import featureFlags from './core/featureFlags';
import icons from './core/icons';
import themes from './core/themes';

// Plugin components
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  AuthRefreshRedirect,
  ConsentPage,
} from '@appfolio/backstage-plugin-auth';
import { Routes } from '@appfolio/backstage-plugin-navigation';
import { CatalogEntityPage, CatalogIndexPage } from '@backstage/plugin-catalog';
import {
  CatalogPage,
  entityPageContent,
} from '@appfolio/backstage-plugin-catalog-react';
import { DeploymentsPage } from '@appfolio/backstage-plugin-deployments';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from '@appfolio/backstage-plugin-homepage';
import { MetricsPage } from '@appfolio/backstage-plugin-metrics';
import { ScaffolderPage } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@appfolio/backstage-plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import {
  TechDocsHomePageContent,
  techDocsReaderPage,
} from '@appfolio/backstage-plugin-techdocs';
import { TechRadarPage } from '@appfolio/backstage-plugin-tech-radar';
import { ToolsPage, TopologyPage } from '@appfolio/backstage-plugin-explore';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { UtilitiesPage } from '@appfolio/backstage-plugin-utilities-page';

// Plugins
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { badgesPlugin } from '@backstage-community/plugin-badges';
import { catalogGraphPlugin } from '@backstage/plugin-catalog-graph';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { orgPlugin } from '@backstage/plugin-org';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { techdocsPlugin } from '@backstage/plugin-techdocs';

const app = createApp({
  apis,
  plugins: [badgesPlugin, catalogGraphPlugin],
  featureFlags,
  themes,
  icons,
  components: {
    SignInPage,
    NotFoundErrorPage,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <Routes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route
      path="/help"
      element={
        <TechDocsReaderPage
          entityRef={{
            namespace: DEFAULT_NAMESPACE,
            kind: 'Component',
            name: 'developer-portal',
          }}
        />
      }
    >
      {techDocsReaderPage}
    </Route>
    <Route path="/auth/:id" element={<AuthRefreshRedirect />} />
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPageContent}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <TechDocsHomePageContent />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {techDocsReaderPage}
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/tech-radar" element={<TechRadarPage />} />
    <Route path="/search" element={<SearchPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/analytics" element={<MetricsPage />} />
    <Route path="/topology" element={<ExplorePage />}>
      <TopologyPage />
    </Route>
    <Route path="/tools" element={<ExplorePage />}>
      <ToolsPage />
    </Route>
    <Route path="/deployments" element={<DeploymentsPage />} />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/utilities" element={<UtilitiesPage />} />
    <Route path="/oauth2/authorize/:sessionId" element={<ConsentPage />} />
  </Routes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <QueryClientProvider client={queryClient}>
      <AppRouter>
        <Root>{routes}</Root>
      </AppRouter>
    </QueryClientProvider>
  </>,
);
